import { useEffect, useRef, useCallback } from 'react';
import { useAuth } from '@/features/auth/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { useCrossTabSync } from './useCrossTabSync';

/**
 * Hook para rastrear a última vez que o usuário esteve ativo no sistema
 *
 * Agora é TAB-AWARE: usa localStorage para deduplicar updates entre abas,
 * evitando múltiplas escritas no banco quando há várias abas abertas.
 *
 * Atualiza last_seen_at em:
 * - Montagem do componente (page load, navigation)
 * - Heartbeat periódico (a cada 5 minutos)
 *
 * Uso: Adicionar em MainLayout para rastreamento global
 */

// Intervalo do heartbeat: 5 minutos
const HEARTBEAT_INTERVAL = 5 * 60 * 1000; // 5 minutos em milissegundos

// Debounce: Evitar múltiplas atualizações em curto período (global entre abas)
const MIN_UPDATE_INTERVAL = 4 * 60 * 1000; // 4 minutos mínimo entre updates (aumentado para dar margem)

// Chave do localStorage para rastrear última atualização global
const LAST_SEEN_UPDATE_KEY = 'd12-last-seen-db-update';

export function useLastSeen() {
  const { user } = useAuth();
  const intervalIdRef = useRef<NodeJS.Timeout | null>(null);

  // Usar cross-tab sync para verificar atualizações de outras abas
  const { hasRecentLastSeenUpdate, updateLastSeenTimestamp, tabId } = useCrossTabSync();

  /**
   * Verifica se já houve uma atualização recente do lastSeen (de qualquer aba)
   */
  const hasRecentGlobalUpdate = useCallback((): boolean => {
    try {
      const stored = localStorage.getItem(LAST_SEEN_UPDATE_KEY);
      if (stored) {
        const lastUpdate = parseInt(stored, 10);
        return Date.now() - lastUpdate < MIN_UPDATE_INTERVAL;
      }
    } catch {
      // Ignorar erros de localStorage
    }
    return false;
  }, []);

  /**
   * Marca que uma atualização foi feita (compartilhado entre abas)
   */
  const markGlobalUpdate = useCallback(() => {
    try {
      localStorage.setItem(LAST_SEEN_UPDATE_KEY, Date.now().toString());
    } catch {
      // Ignorar erros de localStorage
    }

    // Notificar outras abas via cross-tab sync
    updateLastSeenTimestamp();
  }, [updateLastSeenTimestamp]);

  /**
   * Função para atualizar last_seen_at no banco
   * Inclui verificação global para evitar updates duplicados entre abas
   */
  const updateLastSeen = useCallback(async () => {
    if (!user?.id) return;

    // Verificar se já houve update recente de QUALQUER aba
    if (hasRecentGlobalUpdate()) {
      return;
    }

    // Também verificar via cross-tab sync (redundância)
    if (hasRecentLastSeenUpdate(MIN_UPDATE_INTERVAL)) {
      return;
    }

    try {
      const { error } = await supabase
        .from('equipe')
        .update({ last_seen_at: new Date().toISOString() })
        .eq('auth_user_id', user.id);

      if (error) {
        console.error('[useLastSeen] Error:', error);
      } else {
        // Marcar atualização como feita (global)
        markGlobalUpdate();
      }
    } catch (error) {
      console.error('[useLastSeen] Exception:', error);
    }
  }, [user?.id, hasRecentGlobalUpdate, hasRecentLastSeenUpdate, markGlobalUpdate, tabId]);

  useEffect(() => {
    if (!user?.id) {
      // Limpar interval se usuário fizer logout
      if (intervalIdRef.current) {
        clearInterval(intervalIdRef.current);
        intervalIdRef.current = null;
      }
      return;
    }

    // Atualizar imediatamente ao montar o componente
    // (page load, navigation, refresh)
    // Mas só se não houve update recente de outra aba
    updateLastSeen();

    // Configurar heartbeat periódico
    intervalIdRef.current = setInterval(updateLastSeen, HEARTBEAT_INTERVAL);

    // Cleanup: Limpar interval quando componente desmontar
    return () => {
      if (intervalIdRef.current) {
        clearInterval(intervalIdRef.current);
        intervalIdRef.current = null;
      }
    };
  }, [user?.id, updateLastSeen]); // Re-executar se user.id mudar (login/logout)

  // Hook não retorna nada - efeito colateral apenas
}
